import {
  CHARTS_WITH_AI_VISIBILITY,
  CHARTS_WITH_CONTACT_KEYS,
  CHARTS_WITH_EXPORT_OPTIONS,
  CHARTS_WITH_FIXED_MEDIUM,
  CHARTS_WITH_FIXED_PLATFORM,
  CHARTS_WITH_GEOGRAPHIC_SPREAD,
  CHARTS_WITH_INSIGHTS_METRICS,
  CHARTS_WITH_INSTAGRAM_HANDLE_SELECTION,
  CHARTS_WITH_LINKED_SOCIAL_STREAMS,
  CHARTS_WITH_MEDIA_RELEASES_HEADER,
  CHARTS_WITH_MEDIA_RELEASES_ONLY,
  CHARTS_WITH_ONLY_AI_VISIBILITY,
  CHARTS_WITH_OPTIONAL_LINKED_SOCIAL_STREAMS,
  CHARTS_WITH_OPTIONAL_MENTION_STREAMS,
  CHARTS_WITH_OUTREACH_ACTIVITY_SELECTION,
  CHARTS_WITH_OUTREACH_INSIGHTS_METRICS,
  CHARTS_WITH_OUTREACH_PERFORMANCE_METRICS,
  CHARTS_WITH_POST_SELECTION,
  CHARTS_WITH_SINGLE_MEDIA_RELEASE,
  CHARTS_WITH_SOCIAL_ACCOUNT_SELECTION,
  CHARTS_WITH_SOCIAL_ENGAGE_INSIGHTS_METRICS,
  CHARTS_WITH_SOCIAL_ENGAGE_ONLY,
  CHARTS_WITH_SOCIAL_ENGAGE_PERFORMANCE_METRICS,
  CHARTS_WITH_SOCIAL_INSIGHTS_METRICS,
  CHARTS_WITH_SOCIAL_STREAMS_ONLY,
  CHARTS_WITH_SORT_BY,
  CHARTS_WITH_SOV_METRIC_TYPE,
  CHARTS_WITH_STREAM_INSIGHTS_METRICS,
  CHARTS_WITH_STREAMS_AND_KEY_MESSAGES,
  CHARTS_WITH_STREAMS_AND_MEDIA_RELEASES,
  CHARTS_WITH_STREAMS_AND_SOCIAL_STREAMS,
  CHARTS_WITH_STREAMS_COMPARISON,
} from "shared/modules/Charts/constants";
import type {
  ChartSetting,
  ChartSettingWithAIVisibility,
  ChartSettingWithAnalyticsIntegration,
  ChartSettingWithContactKeys,
  ChartSettingWithExportOptions,
  ChartSettingWithInsightsMetrics,
  ChartSettingWithInstagramHandleSelection,
  ChartSettingWithKeyMessages,
  ChartSettingWithLinkedStreams,
  ChartSettingWithMediaReleases,
  ChartSettingWithOptionalLinkedStreams,
  ChartSettingWithOutreachActivityFilters,
  ChartSettingWithOutreachInsightsMetrics,
  ChartSettingWithOutreachPerformanceMetrics,
  ChartSettingWithPostSelection,
  ChartSettingWithSingleMediaRelease,
  ChartSettingWithSocialAccountSelection,
  ChartSettingWithSocialEngageInsightsMetrics,
  ChartSettingWithSocialEngageOnly,
  ChartSettingWithSocialEngagePerformanceMetrics,
  ChartSettingWithSocialInsightsMetrics,
  ChartSettingWithSortBy,
  ChartSettingWithSovMetricType,
  ChartSettingWithStockSymbol,
  ChartSettingWithStreamAndMediaReleases,
  ChartSettingWithStreamComparison,
  ChartSettingWithStreamInsightsMetrics,
  ChartSettingWithStreamsAndMediaTypes,
  ChartsWithInsightsMetrics,
  ChartsWithInstagramHandleSelection,
  ChartsWithOutreachActivitySelection,
  ChartsWithOutreachInsightsMetrics,
  ChartsWithOutreachPerformanceMetrics,
  ChartsWithPostSelection,
  ChartsWithSocialAccountSelection,
  ChartsWithSocialEngageInsightsMetrics,
  ChartsWithSocialEngagePerformanceMetrics,
  ChartsWithSocialInsightsMetrics,
  ChartsWithSovMetricType,
  ChartsWithStreamInsightsMetrics,
  ChartWithFixedMedium,
  ChartWithFixedPlatform,
  ChartWithOptionalMentionStreams,
  GeographicSpreadChart,
  SavedChartSetting,
} from "shared/modules/Charts/types";
import { TopArticlesMetric } from "shared/modules/Charts/types";
import type {
  AIVisibilityDataTableEvent,
  ChartClickEvent,
  ContactLabelClickEvent,
  DataPoint,
  DataPointClickEvent,
  DatasetClickEvent,
  EmojiCountEvent,
  GeographicSpreadClickEvent,
  MentionBenchmarkTimelineClickEvent,
  MentionBenchmarkTimelineDataPoint,
  TopFacebookPagesEvent,
  TopInfluencersEvent,
  TopPlatformPostsEvent,
  WordCloudClickEvent,
} from "shared/modules/Charts/types/chartInteractions";
import { type InstantInsightsChart } from "shared/modules/Charts/types/chartTypes";

export function isChartSettingWithStreamsAndMediaTypes(
  data?: ChartSetting
): data is ChartSettingWithStreamsAndMediaTypes {
  if (!data) {
    return false;
  }

  if (data?.chart) {
    return (
      !CHARTS_WITH_ONLY_AI_VISIBILITY.includes(data.chart) &&
      !CHARTS_WITH_MEDIA_RELEASES_ONLY.includes(data.chart) &&
      !CHARTS_WITH_OUTREACH_ACTIVITY_SELECTION.includes(
        data.chart as ChartsWithOutreachActivitySelection
      ) &&
      !CHARTS_WITH_SOCIAL_ENGAGE_ONLY.includes(data.chart) &&
      !CHARTS_WITH_SOCIAL_ENGAGE_PERFORMANCE_METRICS.includes(
        data.chart as ChartsWithSocialEngagePerformanceMetrics
      )
    );
  }

  return "stream_ids" in data;
}

export function isChartSettingWithAnalyticsIntegration(
  data?: ChartSetting
): data is ChartSettingWithAnalyticsIntegration {
  if (!isChartSettingWithStreamsAndMediaTypes(data)) {
    return false;
  }

  return (
    "requires_analytics_integration" in data &&
    data.requires_analytics_integration === true
  );
}

export function isMentionsBenchmarkChart(data: ChartSetting): boolean {
  return data?.chart === "mentions_benchmark";
}

export function isSentimentBenchmarkChart(data: ChartSetting): boolean {
  return data?.chart === "sentiment_benchmark";
}

export function isGeographicSpreadChart(item?: ChartSetting): boolean {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_GEOGRAPHIC_SPREAD.includes(item.chart as GeographicSpreadChart)
  );
}

export function isChartSettingWithStreamComparison(
  data?: ChartSetting
): data is ChartSettingWithStreamComparison {
  if (!data) {
    return false;
  }

  return Boolean(
    data?.chart && CHARTS_WITH_STREAMS_COMPARISON.includes(data.chart)
  );
}

export function isChartSettingWithMediaReleases(
  data?: ChartSetting
): data is ChartSettingWithMediaReleases {
  if (!data) {
    return false;
  }

  if (data?.chart) {
    return (
      CHARTS_WITH_MEDIA_RELEASES_ONLY.includes(data.chart) ||
      isChartSettingWithStreamAndMediaReleases(data)
    );
  }

  return "media_release_ids" in data;
}

export function isChartSettingWithStreamAndMediaReleases(
  data?: ChartSetting
): data is ChartSettingWithStreamAndMediaReleases {
  if (!data) {
    return false;
  }

  if (data.chart) {
    return CHARTS_WITH_STREAMS_AND_MEDIA_RELEASES.includes(data.chart);
  }

  return "media_release_ids" in data && "stream_ids" in data && "types" in data;
}

export function isChartSettingWithOutreachActivityFilters(
  data?: ChartSetting
): data is ChartSettingWithOutreachActivityFilters {
  return Boolean(data && isOutreachActivitySelectionChart(data.chart));
}

export function isOutreachActivitySelectionChart(
  chart?: ChartSetting["chart"]
): chart is ChartsWithOutreachActivitySelection {
  return Boolean(
    chart &&
    CHARTS_WITH_OUTREACH_ACTIVITY_SELECTION.includes(
      chart as ChartsWithOutreachActivitySelection
    )
  );
}

export function isChartSettingWithSingleMediaRelease(
  data?: ChartSetting
): data is ChartSettingWithSingleMediaRelease {
  if (!data) {
    return false;
  }

  if (data?.chart) {
    return CHARTS_WITH_SINGLE_MEDIA_RELEASE.includes(data.chart);
  }

  return "media_release_id" in data;
}

export function isChartSettingWithExportOptions(
  data?: ChartSetting
): data is ChartSettingWithExportOptions {
  if (!data) {
    return false;
  }

  if (data?.chart) {
    return CHARTS_WITH_EXPORT_OPTIONS.includes(data.chart);
  }

  return "export_options" in data;
}

export function isChartSettingWithKeyMessages(
  data?: ChartSetting
): data is ChartSettingWithKeyMessages {
  if (!data) {
    return false;
  }

  if (data?.chart) {
    return CHARTS_WITH_STREAMS_AND_KEY_MESSAGES.includes(data.chart);
  }

  return "queries" in data;
}

export function isChartSettingWithMentionStreamsAndSocialStreams(
  data?: ChartSetting
): data is ChartSettingWithStreamsAndMediaTypes {
  if (data?.chart) {
    return CHARTS_WITH_STREAMS_AND_SOCIAL_STREAMS.includes(data.chart);
  }

  return false;
}

export function isChartSettingWithSocialStreamsOnly(
  data?: ChartSetting
): data is ChartSettingWithStreamsAndMediaTypes {
  if (data?.chart) {
    return CHARTS_WITH_SOCIAL_STREAMS_ONLY.includes(data.chart);
  }

  return false;
}

export function isChartSettingWithLinkedSocialStreams(
  data?: ChartSetting
): data is ChartSettingWithLinkedStreams {
  if (!data) {
    return false;
  }

  if (data?.chart) {
    return CHARTS_WITH_LINKED_SOCIAL_STREAMS.includes(data.chart);
  }

  return "linked_stream_ids" in data;
}

export function isChartSettingWithOptionalLinkedSocialStreams(
  data?: ChartSetting
): data is ChartSettingWithOptionalLinkedStreams {
  if (!data) {
    return false;
  }

  if (data?.chart) {
    return CHARTS_WITH_OPTIONAL_LINKED_SOCIAL_STREAMS.includes(data.chart);
  }

  return false;
}

export function isChartSettingWithStockSymbol(
  data: ChartSetting
): data is ChartSettingWithStockSymbol {
  if (data?.chart) {
    return data.chart === "coverage_volume_and_stock_price";
  }

  return "stock_symbol" in data;
}

export function isChartSettingWithSortBy(
  data?: ChartSetting
): data is ChartSettingWithSortBy {
  if (!data) {
    return false;
  }

  if (data?.chart) {
    return CHARTS_WITH_SORT_BY.includes(data.chart);
  }

  return "sort_by" in data;
}

export function isChartSettingWithAIVisibility(
  data?: ChartSetting
): data is ChartSettingWithAIVisibility {
  if (data?.chart) {
    return CHARTS_WITH_AI_VISIBILITY.includes(data.chart);
  }

  return false;
}

export function isChartSettingWithOptionalMentionStreams(
  data?: ChartSetting
): data is ChartSettingWithStreamsAndMediaTypes & {
  chart: ChartWithOptionalMentionStreams;
} {
  return Boolean(
    data &&
    data.chart &&
    CHARTS_WITH_OPTIONAL_MENTION_STREAMS.includes(
      data.chart as ChartWithOptionalMentionStreams
    ) &&
    isChartSettingWithStreamsAndMediaTypes(data)
  );
}

export function isChartSettingWithContactKeys(
  data?: ChartSetting
): data is ChartSettingWithContactKeys {
  if (data?.chart) {
    return CHARTS_WITH_CONTACT_KEYS.includes(data.chart);
  }

  return false;
}

export function hasIntentionalEmptyMentionStreams(
  data?: ChartSetting
): data is ChartSettingWithStreamsAndMediaTypes & {
  chart: ChartWithOptionalMentionStreams;
  stream_ids: [];
} {
  return Boolean(
    isChartSettingWithOptionalMentionStreams(data) &&
    Array.isArray(data.stream_ids) &&
    data.stream_ids.length === 0
  );
}

export function isChartSettingWithOnlyAIVisibility(
  data?: ChartSetting
): data is ChartSettingWithAIVisibility {
  if (data?.chart) {
    return CHARTS_WITH_ONLY_AI_VISIBILITY.includes(data.chart);
  }

  return false;
}

export function isTopArticlesMetric(
  value?: string
): value is TopArticlesMetric {
  return Object.values(TopArticlesMetric).includes(value as TopArticlesMetric);
}

export function isChartSettingWithMediaReleasesHeader(
  item?: ChartSetting
): item is ChartSettingWithMediaReleases {
  return Boolean(
    isChartSettingWithMediaReleases(item) &&
    CHARTS_WITH_MEDIA_RELEASES_HEADER.includes(
      item.chart! as InstantInsightsChart
    )
  );
}

export function isChartSettingWithStreamsHeader(
  item: SavedChartSetting
): item is SavedChartSetting<ChartSettingWithStreamsAndMediaTypes> {
  if (
    isMentionsBenchmarkChart(item) ||
    isSentimentBenchmarkChart(item) ||
    item.hide_streams
  ) {
    return false;
  }

  return isChartSettingWithStreamsAndMediaTypes(item);
}

export function isChartSettingWithFixedMedium(
  item?: ChartSetting
): item is ChartSettingWithStreamsAndMediaTypes {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_FIXED_MEDIUM.includes(item.chart as ChartWithFixedMedium) &&
    isChartSettingWithStreamsAndMediaTypes(item)
  );
}

export function isChartSettingWithFixedPlatform(
  item?: ChartSetting
): item is ChartSettingWithStreamsAndMediaTypes {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_FIXED_PLATFORM.includes(item.chart as ChartWithFixedPlatform) &&
    isChartSettingWithStreamsAndMediaTypes(item)
  );
}

export function isChartSettingWithStreamInsightsMetrics(
  item?: ChartSetting
): item is ChartSettingWithStreamInsightsMetrics {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_STREAM_INSIGHTS_METRICS.includes(
      item.chart as ChartsWithStreamInsightsMetrics
    )
  );
}

export function isChartSettingWithOutreachInsightsMetrics(
  item?: ChartSetting
): item is ChartSettingWithOutreachInsightsMetrics {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_OUTREACH_INSIGHTS_METRICS.includes(
      item.chart as ChartsWithOutreachInsightsMetrics
    )
  );
}

export function isChartSettingWithOutreachPerformanceMetrics(
  item?: ChartSetting
): item is ChartSettingWithOutreachPerformanceMetrics {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_OUTREACH_PERFORMANCE_METRICS.includes(
      item.chart as ChartsWithOutreachPerformanceMetrics
    )
  );
}

export function isChartSettingWithSocialEngagePerformanceMetrics(
  item?: ChartSetting
): item is ChartSettingWithSocialEngagePerformanceMetrics {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_SOCIAL_ENGAGE_PERFORMANCE_METRICS.includes(
      item.chart as ChartsWithSocialEngagePerformanceMetrics
    )
  );
}

export function isChartSettingWithSocialEngageInsightsMetrics(
  item?: ChartSetting
): item is ChartSettingWithSocialEngageInsightsMetrics {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_SOCIAL_ENGAGE_INSIGHTS_METRICS.includes(
      item.chart as ChartsWithSocialEngageInsightsMetrics
    )
  );
}

export function isChartSettingWithSocialInsightsMetrics(
  item?: ChartSetting
): item is ChartSettingWithSocialInsightsMetrics {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_SOCIAL_INSIGHTS_METRICS.includes(
      item.chart as ChartsWithSocialInsightsMetrics
    )
  );
}

export function isChartSettingWithInsightsMetrics(
  item?: ChartSetting
): item is ChartSettingWithInsightsMetrics {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_INSIGHTS_METRICS.includes(
      item.chart as ChartsWithInsightsMetrics
    )
  );
}

export function isChartSettingWithSocialEngageOnly(
  item?: ChartSetting
): item is ChartSettingWithSocialEngageOnly {
  return Boolean(
    item?.chart && CHARTS_WITH_SOCIAL_ENGAGE_ONLY.includes(item.chart)
  );
}

export function isChartSettingWithPostSelection(
  item?: ChartSetting
): item is ChartSettingWithPostSelection {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_POST_SELECTION.includes(item.chart as ChartsWithPostSelection)
  );
}

export function isChartSettingWithSocialAccountSelection(
  item?: ChartSetting
): item is ChartSettingWithSocialAccountSelection {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_SOCIAL_ACCOUNT_SELECTION.includes(
      item.chart as ChartsWithSocialAccountSelection
    )
  );
}

export function isChartSettingWithInstagramHandleSelection(
  item?: ChartSetting
): item is ChartSettingWithInstagramHandleSelection {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_INSTAGRAM_HANDLE_SELECTION.includes(
      item.chart as ChartsWithInstagramHandleSelection
    )
  );
}

export function isTopPlatformPostsEvent(
  event: ChartClickEvent
): event is TopPlatformPostsEvent {
  return "attribute" in event && "data" in event;
}

export function isContactLabelClickEvent(
  data: ChartClickEvent
): data is ContactLabelClickEvent {
  return (
    "action" in data &&
    data.action === "contactLabelClicked" &&
    "contactKey" in data &&
    typeof data.contactKey === "string"
  );
}

export function isDataPoint(data: unknown): data is DataPoint {
  return (
    typeof data === "object" &&
    data !== null &&
    "value" in data &&
    "dataIndex" in data &&
    "datasetIndex" in data
  );
}

export function isDataPointClickEvent(
  data: ChartClickEvent
): data is DataPointClickEvent {
  return Object.values(data).every(
    (value) =>
      typeof value === "object" &&
      value !== null &&
      Object.values(value).every(isDataPoint)
  );
}

export function isDatasetClickEvent(
  data: ChartClickEvent
): data is DatasetClickEvent {
  return Object.values(data).every(isDataPoint);
}

export function isWordCloudClickEvent(
  data: ChartClickEvent
): data is WordCloudClickEvent {
  return "word" in data && "weight" in data;
}

export function isGeographicSpreadClickEvent(
  data: ChartClickEvent
): data is GeographicSpreadClickEvent {
  return "location" in data && "location_id" in data;
}

export function isMentionBenchmarkTimelineClickEvent(
  data: ChartClickEvent
): data is MentionBenchmarkTimelineClickEvent {
  return Object.values(data).every(
    (value) =>
      typeof value === "object" &&
      value !== null &&
      (Object.values(value) as MentionBenchmarkTimelineDataPoint[]).every(
        (point) =>
          point &&
          typeof point === "object" &&
          "value" in point &&
          point.value &&
          typeof point.value === "object" &&
          "date" in point.value &&
          "y" in point.value
      )
  );
}

export function isEmojiCountClickEvent(
  data: ChartClickEvent
): data is EmojiCountEvent {
  return "emoji" in data && "total" in data;
}

export function isAIVisibilityTableDataEvent(
  data: ChartClickEvent
): data is AIVisibilityDataTableEvent {
  return (
    "column" in data && "config" in data && "index" in data && "value" in data
  );
}

export function isTopFacebookPagesEvent(
  data: ChartClickEvent
): data is TopFacebookPagesEvent {
  return "likes" in data && "page" in data;
}

export function isTopInfluencersEvent(
  data: ChartClickEvent
): data is TopInfluencersEvent {
  return "followers" in data && "influencer" in data;
}

export function isChartSettingWithSovMetricType(
  item?: ChartSetting
): item is ChartSettingWithSovMetricType {
  return Boolean(
    item?.chart &&
    CHARTS_WITH_SOV_METRIC_TYPE.includes(item.chart as ChartsWithSovMetricType)
  );
}
